import { getAuth } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  type FieldValue,
  type Timestamp,
} from 'firebase/firestore';

import type { DeckAiAssessment } from '@mtgcraft/shared';

import { planDeckCardAddition, planDeckCardMoves, type DeckCardMovePlan } from '@/lib/deckCardMerge';
import type { Deck, DeckCard, DeckFormat, DeckStatus, DeckZone } from '@/types/deck';
import { firestore } from './firebase';

// ============================================================================
// Auth Helper
// ============================================================================

function getCurrentUserId(): string {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('User must be authenticated');
  return user.uid;
}

// ============================================================================
// Firestore Types (internal representation)
// ============================================================================

interface FirestoreDeck {
  name: string;
  description: string | null;
  format: DeckFormat | null;
  status: DeckStatus;
  cover_card_id: string | null;
  enforce_legality: boolean;
  is_locked: boolean;
  card_count: number;
  ai_assessment?: DeckAiAssessment | null;
  created_at: Timestamp | FieldValue;
  updated_at: Timestamp | FieldValue;
}

interface FirestoreDeckCard {
  deck_id: string;
  scryfall_id: string;
  card_name: string | null;
  quantity: number;
  zone: DeckZone;
  added_at: Timestamp | FieldValue;
}

// ============================================================================
// Collection Helpers
// ============================================================================

function getUserDecksCollection(userId: string) {
  return collection(firestore, 'users', userId, 'decks');
}

function getDeckCardsCollection(userId: string) {
  return collection(firestore, 'users', userId, 'deck_cards');
}

function getDeckDocRef(userId: string, deckId: string) {
  return doc(firestore, 'users', userId, 'decks', deckId);
}

function getDeckCardDocRef(userId: string, cardId: string) {
  return doc(firestore, 'users', userId, 'deck_cards', cardId);
}

// ============================================================================
// Type Conversions
// ============================================================================

function firestoreToDeck(id: string, data: FirestoreDeck): Deck {
  return {
    id,
    name: data.name,
    description: data.description ?? null,
    format: data.format ?? null,
    status: data.status,
    cover_card_id: data.cover_card_id ?? null,
    enforce_legality: data.enforce_legality ?? true,
    is_locked: data.is_locked ?? false,
    card_count: data.card_count ?? 0,
    ai_assessment: data.ai_assessment ?? null,
    created_at: (data.created_at as Timestamp)?.toDate() ?? new Date(),
    updated_at: (data.updated_at as Timestamp)?.toDate() ?? new Date(),
  };
}

function firestoreToDeckCard(id: string, data: FirestoreDeckCard): DeckCard {
  return {
    id,
    deck_id: data.deck_id,
    scryfall_id: data.scryfall_id,
    card_name: data.card_name ?? null,
    quantity: data.quantity,
    zone: data.zone,
    added_at: (data.added_at as Timestamp)?.toDate() ?? new Date(),
  };
}

// ============================================================================
// Deck CRUD Operations
// ============================================================================

export interface CreateDeckInput {
  name: string;
  description?: string | null;
  format?: DeckFormat | null;
  status?: DeckStatus;
  enforce_legality?: boolean;
}

export async function createDeck(input: CreateDeckInput): Promise<Deck> {
  const userId = getCurrentUserId();
  const decksRef = getUserDecksCollection(userId);
  const newDeckRef = doc(decksRef);

  const deckData: Omit<FirestoreDeck, 'id'> = {
    name: input.name,
    description: input.description ?? null,
    format: input.format ?? null,
    status: input.status ?? 'brewing',
    cover_card_id: null,
    enforce_legality: input.enforce_legality ?? true,
    is_locked: false,
    card_count: 0,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  await setDoc(newDeckRef, deckData);

  return {
    id: newDeckRef.id,
    name: deckData.name,
    description: deckData.description,
    format: deckData.format,
    status: deckData.status,
    cover_card_id: deckData.cover_card_id,
    enforce_legality: deckData.enforce_legality,
    is_locked: false,
    card_count: 0,
    created_at: new Date(),
    updated_at: new Date(),
  };
}

export async function getDeck(deckId: string): Promise<Deck | null> {
  const userId = getCurrentUserId();
  const deckRef = getDeckDocRef(userId, deckId);
  const snap = await getDoc(deckRef);

  if (!snap.exists()) return null;

  return firestoreToDeck(snap.id, snap.data() as FirestoreDeck);
}

export async function getAllDecks(): Promise<Deck[]> {
  const userId = getCurrentUserId();
  const decksRef = getUserDecksCollection(userId);
  const q = query(decksRef, orderBy('updated_at', 'desc'));
  const snap = await getDocs(q);

  return snap.docs.map((d) => firestoreToDeck(d.id, d.data() as FirestoreDeck));
}

export async function getDecksByStatus(status: DeckStatus): Promise<Deck[]> {
  const allDecks = await getAllDecks();
  return allDecks.filter((d) => d.status === status);
}

export interface UpdateDeckInput {
  name?: string;
  description?: string | null;
  format?: DeckFormat | null;
  status?: DeckStatus;
  cover_card_id?: string | null;
  enforce_legality?: boolean;
  is_locked?: boolean;
}

export async function updateDeck(deckId: string, input: UpdateDeckInput): Promise<void> {
  const userId = getCurrentUserId();
  const deckRef = getDeckDocRef(userId, deckId);

  const updates: Record<string, unknown> = {
    updated_at: serverTimestamp(),
  };

  if (input.name !== undefined) updates.name = input.name;
  if (input.description !== undefined) updates.description = input.description;
  if (input.format !== undefined) updates.format = input.format;
  if (input.status !== undefined) updates.status = input.status;
  if (input.cover_card_id !== undefined) updates.cover_card_id = input.cover_card_id;
  if (input.enforce_legality !== undefined) updates.enforce_legality = input.enforce_legality;
  if (input.is_locked !== undefined) updates.is_locked = input.is_locked;

  await updateDoc(deckRef, updates);
}

export async function deleteDeck(deckId: string): Promise<void> {
  const userId = getCurrentUserId();

  // Query and delete all cards with this deck_id
  const cardsRef = getDeckCardsCollection(userId);
  const cardsQuery = query(cardsRef, where('deck_id', '==', deckId));
  const cardsSnap = await getDocs(cardsQuery);

  const batch = writeBatch(firestore);

  cardsSnap.docs.forEach((cardDoc) => {
    batch.delete(cardDoc.ref);
  });

  // Then delete the deck itself
  const deckRef = getDeckDocRef(userId, deckId);
  batch.delete(deckRef);

  await batch.commit();
}

export async function duplicateDeck(deckId: string, newName?: string): Promise<Deck> {
  const userId = getCurrentUserId();

  // Get the original deck
  const originalDeck = await getDeck(deckId);
  if (!originalDeck) throw new Error('Deck not found');

  // Get all cards from the original deck
  const cards = await getDeckCards(deckId);

  // Create the new deck
  const newDeck = await createDeck({
    name: newName ?? `${originalDeck.name} (Copy)`,
    description: originalDeck.description,
    format: originalDeck.format,
    status: 'brewing', // Always start as brewing
    enforce_legality: originalDeck.enforce_legality,
  });

  // Copy all cards to the new deck
  if (cards.length > 0) {
    const batch = writeBatch(firestore);
    const cardsRef = getDeckCardsCollection(userId);

    cards.forEach((card) => {
      const newCardRef = doc(cardsRef);
      batch.set(newCardRef, {
        deck_id: newDeck.id,
        scryfall_id: card.scryfall_id,
        card_name: card.card_name,
        quantity: card.quantity,
        zone: card.zone,
        added_at: serverTimestamp(),
      });
    });

    // Update the card count
    const deckRef = getDeckDocRef(userId, newDeck.id);
    batch.update(deckRef, {
      card_count: cards.reduce((sum, c) => sum + c.quantity, 0),
      updated_at: serverTimestamp(),
    });

    await batch.commit();
  }

  return newDeck;
}

// ============================================================================
// Deck Card Operations
// ============================================================================

export async function getDeckCards(deckId: string): Promise<DeckCard[]> {
  const userId = getCurrentUserId();
  const cardsRef = getDeckCardsCollection(userId);
  const cardsQuery = query(cardsRef, where('deck_id', '==', deckId));
  const snap = await getDocs(cardsQuery);

  return snap.docs.map((d) => firestoreToDeckCard(d.id, d.data() as FirestoreDeckCard));
}

export interface AddDeckCardInput {
  scryfall_id: string;
  card_name?: string | null;
  quantity?: number;
  zone?: DeckZone;
}

export async function addDeckCard(deckId: string, input: AddDeckCardInput): Promise<DeckCard> {
  const userId = getCurrentUserId();
  const cardsRef = getDeckCardsCollection(userId);
  const quantity = input.quantity ?? 1;
  const zone = input.zone ?? 'main';

  // Enforce the one-slot-per-(edition, zone) rule: merge into an existing slot
  // when the same edition already lives in the target zone.
  const existingCards = await getDeckCards(deckId);
  const plan = planDeckCardAddition(existingCards, {
    scryfallId: input.scryfall_id,
    cardName: input.card_name,
    zone,
    quantity,
  });

  const batch = writeBatch(firestore);
  const deckRef = getDeckDocRef(userId, deckId);
  const deckSnap = await getDoc(deckRef);
  const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;

  let resultCardId: string;
  let resultQuantity: number;

  if (plan.type === 'increment') {
    const cardRef = getDeckCardDocRef(userId, plan.cardId);
    batch.update(cardRef, { quantity: plan.newQuantity });
    resultCardId = plan.cardId;
    resultQuantity = plan.newQuantity;
  } else {
    const newCardRef = doc(cardsRef);
    batch.set(newCardRef, {
      deck_id: deckId,
      scryfall_id: plan.scryfallId,
      card_name: plan.cardName,
      quantity: plan.quantity,
      zone: plan.zone,
      added_at: serverTimestamp(),
    } satisfies FirestoreDeckCard);
    resultCardId = newCardRef.id;
    resultQuantity = plan.quantity;
  }

  batch.update(deckRef, {
    card_count: currentCount + quantity,
    updated_at: serverTimestamp(),
  });

  await batch.commit();

  return {
    id: resultCardId,
    deck_id: deckId,
    scryfall_id: input.scryfall_id,
    card_name: input.card_name ?? null,
    quantity: resultQuantity,
    zone,
    added_at: new Date(),
  };
}

export async function addDeckCards(deckId: string, inputs: AddDeckCardInput[]): Promise<DeckCard[]> {
  if (inputs.length === 0) return [];

  const userId = getCurrentUserId();
  const cardsRef = getDeckCardsCollection(userId);
  const deckRef = getDeckDocRef(userId, deckId);
  const BATCH_SIZE = 500;

  const results: DeckCard[] = [];
  let totalAdded = 0;

  // Split into chunks of BATCH_SIZE to stay within Firestore's 500-doc batch limit.
  // Reserve 1 slot per chunk for the deck doc update.
  for (let i = 0; i < inputs.length; i += BATCH_SIZE - 1) {
    const chunk = inputs.slice(i, i + BATCH_SIZE - 1);
    const batch = writeBatch(firestore);
    let chunkAdded = 0;

    for (const input of chunk) {
      const newCardRef = doc(cardsRef);
      const quantity = input.quantity ?? 1;

      const cardData: FirestoreDeckCard = {
        deck_id: deckId,
        scryfall_id: input.scryfall_id,
        card_name: input.card_name ?? null,
        quantity,
        zone: input.zone ?? 'main',
        added_at: serverTimestamp(),
      };

      batch.set(newCardRef, cardData);
      chunkAdded += quantity;

      results.push({
        id: newCardRef.id,
        deck_id: deckId,
        scryfall_id: cardData.scryfall_id,
        card_name: cardData.card_name,
        quantity: cardData.quantity,
        zone: cardData.zone,
        added_at: new Date(),
      });
    }

    // Update deck's card_count incrementally per batch
    const deckSnap = await getDoc(deckRef);
    const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
    batch.update(deckRef, {
      card_count: currentCount + chunkAdded,
      updated_at: serverTimestamp(),
    });

    await batch.commit();
    totalAdded += chunkAdded;
  }

  return results;
}

/**
 * Add multiple cards to a deck while enforcing the one-slot-per-(edition, zone)
 * rule: inputs that match an existing slot (or each other) are merged into a
 * single slot instead of creating duplicates (see {@link planDeckCardAddition}).
 * Existing deck cards are read once, then adds/updates are written in batches.
 */
export async function addOrMergeDeckCards(deckId: string, inputs: AddDeckCardInput[]): Promise<void> {
  if (inputs.length === 0) return;

  const userId = getCurrentUserId();
  const cardsRef = getDeckCardsCollection(userId);
  const deckRef = getDeckDocRef(userId, deckId);
  const BATCH_SIZE = 500;

  // Working set of slots (existing + synthesized new) keyed by (scryfall_id, zone).
  const existing = await getDeckCards(deckId);
  const originalQuantity = new Map(existing.map((c) => [c.id, c.quantity]));
  const slots = existing.map((c) => ({
    id: c.id,
    scryfall_id: c.scryfall_id,
    zone: c.zone,
    quantity: c.quantity,
    card_name: c.card_name as string | null,
    isNew: false,
  }));

  let totalAdded = 0;
  for (const input of inputs) {
    const quantity = input.quantity ?? 1;
    const zone = input.zone ?? 'main';
    totalAdded += quantity;

    const slot = slots.find((s) => s.scryfall_id === input.scryfall_id && s.zone === zone);
    if (slot) {
      slot.quantity += quantity;
    } else {
      const newRef = doc(cardsRef);
      slots.push({
        id: newRef.id,
        scryfall_id: input.scryfall_id,
        zone,
        quantity,
        card_name: input.card_name ?? null,
        isNew: true,
      });
    }
  }

  // Derive write operations: new slots → set; touched existing slots → update.
  type Op =
    | { type: 'set'; id: string; scryfall_id: string; zone: DeckZone; quantity: number; card_name: string | null }
    | { type: 'update'; id: string; quantity: number };
  const ops: Op[] = [];
  for (const slot of slots) {
    if (slot.isNew) {
      ops.push({
        type: 'set',
        id: slot.id,
        scryfall_id: slot.scryfall_id,
        zone: slot.zone,
        quantity: slot.quantity,
        card_name: slot.card_name,
      });
    } else if (originalQuantity.get(slot.id) !== slot.quantity) {
      ops.push({ type: 'update', id: slot.id, quantity: slot.quantity });
    }
  }
  if (ops.length === 0) return;

  let remainingCountDelta = totalAdded;
  for (let i = 0; i < ops.length; i += BATCH_SIZE - 1) {
    const chunk = ops.slice(i, i + BATCH_SIZE - 1);
    const batch = writeBatch(firestore);

    for (const op of chunk) {
      const cardRef = getDeckCardDocRef(userId, op.id);
      if (op.type === 'set') {
        batch.set(cardRef, {
          deck_id: deckId,
          scryfall_id: op.scryfall_id,
          card_name: op.card_name,
          quantity: op.quantity,
          zone: op.zone,
          added_at: serverTimestamp(),
        } satisfies FirestoreDeckCard);
      } else {
        batch.update(cardRef, { quantity: op.quantity });
      }
    }

    // Apply the full count delta on the first chunk only (it is a single total).
    const deckSnap = await getDoc(deckRef);
    const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
    batch.update(deckRef, {
      card_count: currentCount + remainingCountDelta,
      updated_at: serverTimestamp(),
    });
    remainingCountDelta = 0;

    await batch.commit();
  }
}

export async function bulkAddOrUpdateDeckCards(
  deckId: string,
  toAdd: AddDeckCardInput[],
  toUpdate: { cardId: string; newQuantity: number }[],
): Promise<void> {
  if (toAdd.length === 0 && toUpdate.length === 0) return;

  const userId = getCurrentUserId();
  const cardsRef = getDeckCardsCollection(userId);
  const deckRef = getDeckDocRef(userId, deckId);
  const BATCH_SIZE = 500;

  // Build a flat list of batch operations
  type BatchOp = { type: 'add'; input: AddDeckCardInput } | { type: 'update'; cardId: string; newQuantity: number };

  const ops: BatchOp[] = [
    ...toAdd.map((input) => ({ type: 'add' as const, input })),
    ...toUpdate.map((u) => ({ type: 'update' as const, cardId: u.cardId, newQuantity: u.newQuantity })),
  ];

  // Process in chunks, reserving 1 slot per chunk for the deck count update
  for (let i = 0; i < ops.length; i += BATCH_SIZE - 1) {
    const chunk = ops.slice(i, i + BATCH_SIZE - 1);
    const batch = writeBatch(firestore);
    let countDelta = 0;

    for (const op of chunk) {
      if (op.type === 'add') {
        const newCardRef = doc(cardsRef);
        const quantity = op.input.quantity ?? 1;
        batch.set(newCardRef, {
          deck_id: deckId,
          scryfall_id: op.input.scryfall_id,
          card_name: op.input.card_name ?? null,
          quantity,
          zone: op.input.zone ?? 'main',
          added_at: serverTimestamp(),
        } satisfies FirestoreDeckCard);
        countDelta += quantity;
      } else {
        const cardRef = getDeckCardDocRef(userId, op.cardId);
        // We only know the new quantity; compute delta from old quantity via the caller
        // (caller already accounts for this). We set absolute quantity here.
        batch.update(cardRef, { quantity: op.newQuantity });
        // Delta is +1 per update (caller ensures each update increments by 1)
        countDelta += 1;
      }
    }

    const deckSnap = await getDoc(deckRef);
    const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
    batch.update(deckRef, {
      card_count: currentCount + countDelta,
      updated_at: serverTimestamp(),
    });

    await batch.commit();
  }
}

export interface UpdateDeckCardInput {
  quantity?: number;
  zone?: DeckZone;
  /** New scryfall_id for changing card version */
  scryfall_id?: string;
  /** Updated card name (should match the new version) */
  card_name?: string | null;
}

export async function updateDeckCard(deckId: string, cardId: string, input: UpdateDeckCardInput): Promise<void> {
  const userId = getCurrentUserId();
  const cardRef = getDeckCardDocRef(userId, cardId);
  const deckRef = getDeckDocRef(userId, deckId);

  // Get current card data for quantity diff
  const cardSnap = await getDoc(cardRef);
  if (!cardSnap.exists()) throw new Error('Card not found in deck');

  const currentCard = cardSnap.data() as FirestoreDeckCard;
  const quantityDiff = (input.quantity ?? currentCard.quantity) - currentCard.quantity;

  const batch = writeBatch(firestore);

  const updates: Record<string, unknown> = {};
  if (input.quantity !== undefined) updates.quantity = input.quantity;
  if (input.zone !== undefined) updates.zone = input.zone;
  if (input.scryfall_id !== undefined) updates.scryfall_id = input.scryfall_id;
  if (input.card_name !== undefined) updates.card_name = input.card_name;

  if (Object.keys(updates).length > 0) {
    batch.update(cardRef, updates);
  }

  // Update deck card count if quantity changed
  if (quantityDiff !== 0) {
    const deckSnap = await getDoc(deckRef);
    const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
    batch.update(deckRef, {
      card_count: Math.max(0, currentCount + quantityDiff),
      updated_at: serverTimestamp(),
    });
  }

  await batch.commit();
}

export async function removeDeckCard(deckId: string, cardId: string): Promise<void> {
  const userId = getCurrentUserId();
  const cardRef = getDeckCardDocRef(userId, cardId);
  const deckRef = getDeckDocRef(userId, deckId);

  // Get quantity for count update
  const cardSnap = await getDoc(cardRef);
  if (!cardSnap.exists()) return;

  const cardData = cardSnap.data() as FirestoreDeckCard;

  const batch = writeBatch(firestore);
  batch.delete(cardRef);

  // Update deck card count
  const deckSnap = await getDoc(deckRef);
  const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
  batch.update(deckRef, {
    card_count: Math.max(0, currentCount - cardData.quantity),
    updated_at: serverTimestamp(),
  });

  await batch.commit();
}

export async function removeDeckCards(deckId: string, cardIds: string[]): Promise<void> {
  if (cardIds.length === 0) return;

  const userId = getCurrentUserId();
  const deckRef = getDeckDocRef(userId, deckId);

  const batch = writeBatch(firestore);
  let totalRemoved = 0;

  for (const cardId of cardIds) {
    const cardRef = getDeckCardDocRef(userId, cardId);
    const cardSnap = await getDoc(cardRef);

    if (cardSnap.exists()) {
      const cardData = cardSnap.data() as FirestoreDeckCard;
      totalRemoved += cardData.quantity;
      batch.delete(cardRef);
    }
  }

  // Update deck card count
  const deckSnap = await getDoc(deckRef);
  const currentCount = (deckSnap.data() as FirestoreDeck | undefined)?.card_count ?? 0;
  batch.update(deckRef, {
    card_count: Math.max(0, currentCount - totalRemoved),
    updated_at: serverTimestamp(),
  });

  await batch.commit();
}

export async function moveDeckCard(deckId: string, cardId: string, toZone: DeckZone): Promise<void> {
  // Route through the merge-aware bulk mover so a move into a zone that already
  // holds the same edition merges quantities instead of creating a duplicate.
  await bulkUpdateDeckCardZones(deckId, [{ cardId, zone: toZone }]);
}

/**
 * Apply a pre-computed deck card move plan in batched writes.
 * A move/merge preserves total quantity, so deck `card_count` is unchanged and
 * only `updated_at` is bumped. Per-card zone + quantity changes are combined
 * into a single update. Chunked to stay within Firestore's 500-doc batch limit
 * (reserving 1 slot per chunk for the deck doc update).
 */
async function applyDeckCardMovePlan(deckId: string, plan: DeckCardMovePlan): Promise<void> {
  const { zoneUpdates, quantityUpdates, deletes } = plan;
  if (zoneUpdates.length === 0 && quantityUpdates.length === 0 && deletes.length === 0) return;

  const userId = getCurrentUserId();
  const deckRef = getDeckDocRef(userId, deckId);
  const BATCH_SIZE = 500;

  // Combine zone + quantity changes per card into a single update payload.
  const updateById = new Map<string, Record<string, unknown>>();
  for (const { cardId, zone } of zoneUpdates) {
    updateById.set(cardId, { ...(updateById.get(cardId) ?? {}), zone });
  }
  for (const { cardId, quantity } of quantityUpdates) {
    updateById.set(cardId, { ...(updateById.get(cardId) ?? {}), quantity });
  }

  type Op = { type: 'update'; cardId: string; data: Record<string, unknown> } | { type: 'delete'; cardId: string };
  const ops: Op[] = [
    ...Array.from(updateById, ([cardId, data]) => ({ type: 'update' as const, cardId, data })),
    ...deletes.map((cardId) => ({ type: 'delete' as const, cardId })),
  ];

  for (let i = 0; i < ops.length; i += BATCH_SIZE - 1) {
    const chunk = ops.slice(i, i + BATCH_SIZE - 1);
    const batch = writeBatch(firestore);

    for (const op of chunk) {
      const cardRef = getDeckCardDocRef(userId, op.cardId);
      if (op.type === 'update') batch.update(cardRef, op.data);
      else batch.delete(cardRef);
    }

    batch.update(deckRef, { updated_at: serverTimestamp() });

    await batch.commit();
  }
}

/**
 * Move multiple deck cards to new zones, merging into existing slots when the
 * same card edition already lives in the target zone (see {@link planDeckCardMoves}).
 * Zone changes do not affect `card_count` (total quantity is preserved).
 */
export async function bulkUpdateDeckCardZones(deckId: string, updates: { cardId: string; zone: DeckZone }[]): Promise<void> {
  if (updates.length === 0) return;

  const cards = await getDeckCards(deckId);
  const plan = planDeckCardMoves(cards, updates);
  await applyDeckCardMovePlan(deckId, plan);
}

export async function setDeckCardQuantity(deckId: string, cardId: string, quantity: number): Promise<void> {
  if (quantity <= 0) {
    await removeDeckCard(deckId, cardId);
  } else {
    await updateDeckCard(deckId, cardId, { quantity });
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Recalculates and updates the card_count for a deck.
 * Useful for fixing inconsistent counts.
 */
export async function recalculateDeckCardCount(deckId: string): Promise<number> {
  const userId = getCurrentUserId();
  const cards = await getDeckCards(deckId);
  const totalCount = cards.reduce((sum, card) => sum + card.quantity, 0);

  const deckRef = getDeckDocRef(userId, deckId);
  await updateDoc(deckRef, {
    card_count: totalCount,
    updated_at: serverTimestamp(),
  });

  return totalCount;
}

/**
 * Gets deck cards grouped by zone.
 */
export async function getDeckCardsByZone(deckId: string): Promise<Record<DeckZone, DeckCard[]>> {
  const cards = await getDeckCards(deckId);

  const byZone: Record<DeckZone, DeckCard[]> = {
    commander: [],
    companion: [],
    main: [],
    sideboard: [],
    maybeboard: [],
    tokens: [],
  };

  for (const card of cards) {
    byZone[card.zone].push(card);
  }

  return byZone;
}

/**
 * Deletes all decks and their cards for the current user.
 * Returns the number of decks deleted.
 */
export async function clearAllDecks(onProgress?: (cleared: number, total: number) => void): Promise<number> {
  const decks = await getAllDecks();

  if (decks.length === 0) return 0;

  const total = decks.length;
  onProgress?.(0, total);

  for (let i = 0; i < decks.length; i++) {
    await deleteDeck(decks[i].id);
    onProgress?.(i + 1, total);
  }

  return decks.length;
}
